import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, Eye, EyeOff, KeyRound, LifeBuoy, HelpCircle, CheckCircle2, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/TenantBrandingContext";
import { useTheme } from "../context/ThemeContext";
import api, { formatErr } from "../lib/api";
import InstallPWAButton from "../components/InstallPWAButton";

// [2026-02-19 — Stage 2] Logo is brand-driven via useBranding(). The
// constant below is the fallback for the unbranded public Rogue brand
// (used when by-domain probe fails or returns no logo). Tenant logo_url
// overrides this once the branding context hydrates.
// Brand-aware fallback logo.
// - Visitors on a HidroVerse hostname see the HV logo even before the
//   by-domain probe completes (avoids "Rogue flash" on slow connections).
// - Everyone else sees the public Rogue/Geminii logo.
const FALLBACK_LOGO_URL = "/brand/rogue-white-blue.png";
const HV_FALLBACK_LOGO_URL =
  "https://customer-assets.emergentagent.com/job_tech-dispatch-55/artifacts/qjyr3rrs_hidroverse%20%285%29.png";
const isHidroVerseHost = () => {
  if (typeof window === "undefined") return false;
  const h = (window.location.host || "").toLowerCase();
  return h.includes("hidroverse") || h.startsWith("hidroverse-rogue.");
};
const pickFallbackLogo = () => (isHidroVerseHost() ? HV_FALLBACK_LOGO_URL : FALLBACK_LOGO_URL);
// [Iter 274] Host-aware TEXT fallbacks. Without these, when the
// /by-domain branding probe is slow OR fails (DNS transition, cold
// PWA, intermittent network), HidroVerse hosts render the HV logo
// next to the literal word "Rogue" — Dirk's screenshot bug. We now
// pair every logo fallback with the matching brand NAME so the two
// can never disagree.
const HV_FALLBACK_APP_NAME = "SalesFlow";
const FALLBACK_APP_NAME = "Rogue";
const pickFallbackAppName = () => (isHidroVerseHost() ? HV_FALLBACK_APP_NAME : FALLBACK_APP_NAME);

const REMEMBER_KEY = "hv_remember_email";

export default function Login() {
  const { user, login, loginScope, claimPassword, demoLogin } = useAuth();
  const { branding } = useBranding();
  const { theme: loginTheme } = useTheme();
  // [Iter 290zp.13t] Theme-aware logo so the white-on-transparent
  // dark variant doesn't disappear on the login page's light surface.
  const LOGO_URL =
    (loginTheme === "light"
      ? (branding?.logo_url_light || branding?.logo_url)
      : (branding?.logo_url_dark || branding?.logo_url))
    || pickFallbackLogo();
  // [Iter 277] Branding context pre-fills `app_name = "Rogue"` from
  // DEFAULTS before the by-domain probe runs. That makes
  // `branding.app_name` always TRUTHY ("Rogue") even when the probe
  // fails entirely (production SSL handshake fail on
  // salesflow.hidroverse.co.za). So a plain `branding?.app_name ||
  // pickFallbackAppName()` short-circuits to "Rogue" on every HV cold
  // load — exactly the bug Dirk is seeing. Fix: when the host
  // pattern says HidroVerse AND the current branding still equals the
  // Rogue default (i.e. by-domain probe hasn't successfully overridden
  // it), force the HV fallback text. Once the probe lands and sets a
  // real tenant brand, that's what shows.
  const _isHV = isHidroVerseHost();
  const _isStillDefault = (branding?.app_name || "").toLowerCase() === "rogue";
  const _useHostFallback = _isHV && _isStillDefault;
  const APP_NAME = _useHostFallback ? HV_FALLBACK_APP_NAME : (branding?.app_name || pickFallbackAppName());
  const BRAND_NAME = APP_NAME;
  // Split "SalesFlow" → "Sales" + "Flow" for the brand-coloured second
  // syllable. Single-word names render as one block.
  const splitAppName = (name) => {
    if (!name || name.length < 5) return { head: name || pickFallbackAppName(), tail: "" };
    const half = Math.ceil(name.length / 2);
    return { head: name.slice(0, half), tail: name.slice(half) };
  };
  const { head: APP_HEAD, tail: APP_TAIL } = splitAppName(APP_NAME);
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBER_KEY) || "");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(() => !!localStorage.getItem(REMEMBER_KEY));
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingHint, setLoadingHint] = useState("");
  // First-login "create password" mode
  const [claimMode, setClaimMode] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  // "Forgot password" request mode
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotNote, setForgotNote] = useState("");
  // Iter 418.aa — user types the desired new password on the request.
  const [forgotNewPassword, setForgotNewPassword] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotErr, setForgotErr] = useState("");
  // [2026-02-19 — Stage 2 part 3] One-click read-only demo.
  const [demoBusy, setDemoBusy] = useState(false);
  // Iter 426 — Multi-customer picker state. When /auth/login returns
  // { scope_required: true, customers: [...] } we hold the password
  // in memory (never persisted) and render a picker instead of
  // navigating.
  const [scopePicker, setScopePicker] = useState(null);
  // 2026-02 — show a friendly banner when the 401 interceptor bounced
  // the user here. The flag is set in lib/api.js (sessionStorage) AND/OR
  // via ?expired=1 query param (in case session storage was wiped).
  const [sessionExpired, setSessionExpired] = useState(false);
  // Iter 426 Phase 3 — When the portal user hits "Switch customer",
  // we log them out and redirect to /login?switch=1 so we can show
  // a friendly hint that they're signing back in to pick a different
  // customer.
  const [switchMode, setSwitchMode] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // [2026-02-19] Self-service signup redirect contract.
    // When /signup completes, it redirects the visitor to
    //   <new-tenant-url>/login#token=<JWT>
    // We hydrate localStorage with the token, strip the fragment,
    // and let /auth/me redirect them into the new workspace.
    const hash = window.location.hash || "";
    const m = hash.match(/[#&]token=([^&]+)/);
    if (m) {
      try {
        localStorage.setItem("hv_token", decodeURIComponent(m[1]));
        // Strip the fragment so the JWT doesn't linger in history.
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        // Hard-reload so AuthContext re-runs /auth/me with the new token.
        window.location.reload();
        return;
      } catch (_) { /* ignore */ }
    }
    const fromStorage = sessionStorage.getItem("hv_session_expired") === "1";
    const fromQuery =
      typeof window !== "undefined" && window.location.search.includes("expired=1");
    if (fromStorage || fromQuery) {
      setSessionExpired(true);
      try { sessionStorage.removeItem("hv_session_expired"); } catch (_) { /* ignore */ }
    }
    // Iter 426 Phase 3 — Detect the "switch customer" flow.
    if (typeof window !== "undefined" && window.location.search.includes("switch=1")) {
      setSwitchMode(true);
    }

    // 2026-02-20 (iter 175, Dirk) — pre-warm the backend.
    // Production containers scale to zero when idle; the first request
    // after idle waits 15-30s for the container to boot. Send a cheap
    // GET on Login page mount so by the time the user types email +
    // password + taps Sign in, the container is already hot. Failures
    // are silent — we'll fall through to the normal login attempt and
    // its (now 30s) timeout + auto-retry will still handle it.
    api.get("/health", { timeout: 30_000 }).catch(() => { /* ignore */ });
  }, []);

  if (user) return <Navigate to="/" replace />;

  const persistRemember = () => {
    if (remember) localStorage.setItem(REMEMBER_KEY, email);
    else localStorage.removeItem(REMEMBER_KEY);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    setLoadingHint("");
    // Iter 175 — if the request takes longer than 3s show a friendly
    // "Waking the server…" hint so the user knows it's not hung.
    // Most cold-starts resolve within 15s; if we hit the 30s/15s
    // backstop the AuthContext-side auto-retry kicks in transparently.
    const hintTimer = setTimeout(() => {
      setLoadingHint("Waking the server… this can take up to 30 seconds.");
    }, 3000);
    try {
      const u = await login(email, password);
      persistRemember();
      // Iter 426 — Multi-customer picker path. `login()` returns
      // a `scope_required` sentinel instead of a user object when
      // the portal user has memberships across multiple customers.
      if (u?.scope_required) {
        setScopePicker(u);
        return;
      }
      // [Iter 290zp.13z14c] Post-login redirect targets:
      //   • client → /portal
      //   • geminii_admin → /_geminii/admin (operator console)
      //   • everyone else → / (CRM home)
      // Without the geminii_admin branch, super-admins land on /
      // which renders the tenant Layout — blank for a tenant_id=null
      // user — and looks like a broken login.
      const target = u?.role === "client"
        ? "/portal"
        : u?.role === "geminii_admin"
          ? "/_geminii/admin"
          : "/";
      navigate(target, { replace: true });
    } catch (ex) {
      // Backend returns HTTP 409 with { detail: { code: "password_not_set" } }
      // when an invited account has no password yet. Switch the form to the
      // "Create your password" view instead of showing an error.
      const status = ex?.response?.status;
      const detail = ex?.response?.data?.detail;
      if (
        status === 409 &&
        ((typeof detail === "object" && detail?.code === "password_not_set") ||
          detail === "password_not_set")
      ) {
        setClaimMode(true);
        setPassword("");
        setErr("");
      } else {
        // 2026-02-20 (iter 174, Dirk) — generic "Login failed" was useless.
        // Distinguish the actual failure mode so the user knows whether to:
        //   • check their password (401 = wrong creds)
        //   • try again in a minute (5xx / timeout = server hiccup)
        //   • check internet (no response at all)
        //   • contact admin (403 = deactivated)
        let msg;
        if (status === 401) {
          msg = "Wrong email or password.";
        } else if (status === 403) {
          msg = typeof detail === "string"
            ? detail
            : "Your account has been deactivated. Contact your administrator.";
        } else if (status === 429) {
          msg = "Too many login attempts. Please wait a minute and try again.";
        } else if (status && status >= 500) {
          msg = "Server error — please try again in a moment.";
        } else if (!ex?.response) {
          // No response object → network / DNS / CORS / timeout.
          msg = ex?.code === "ECONNABORTED"
            ? "The server didn't respond in time. Check your internet and try again."
            : "Couldn't reach the server. Check your internet and try again.";
        } else {
          msg = formatErr(detail, "Login failed");
        }
        setErr(msg);
      }
    } finally {
      clearTimeout(hintTimer);
      setLoading(false);
      setLoadingHint("");
    }
  };

  const handleClaim = async (e) => {
    e.preventDefault();
    setErr("");
    if (newPw.length < 4) {
      setErr("Password must be at least 4 characters.");
      return;
    }
    if (newPw !== newPw2) {
      setErr("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const u = await claimPassword(email, newPw);
      persistRemember();
      const target = u?.role === "client"
        ? "/portal"
        : u?.role === "geminii_admin"
          ? "/_geminii/admin"
          : "/";
      navigate(target, { replace: true });
    } catch (ex) {
      setErr(formatErr(ex?.response?.data?.detail, "Couldn't set password"));
    } finally {
      setLoading(false);
    }
  };

  const openForgot = () => {
    setForgotEmail(email || "");
    setForgotNote("");
    setForgotSent(false);
    setForgotErr("");
    setForgotOpen(true);
  };

  // [2026-02-19 — Stage 2 part 3] "Continue as demo" button. Logs in
  // as the seeded demo user (read-only via demo_guard middleware) and
  // drops the visitor straight on the dashboard. Visible everywhere
  // for now — the demo middleware blocks writes regardless. On a fresh
  // deployment the demo tenant auto-seeds on first hit (~15s); we retry
  // a few times with a clear "Demo is warming up…" message so visitors
  // never see the raw "not seeded" error.
  const continueAsDemo = async () => {
    setDemoBusy(true);
    setErr("");
    const tryLogin = async () => {
      const u = await demoLogin();
      navigate(u?.role === "client" ? "/portal" : "/", { replace: true });
    };
    try {
      await tryLogin();
    } catch (ex) {
      const status = ex?.response?.status;
      // First-deploy auto-seed kicked off — retry after a short delay.
      if (status === 503) {
        setErr("Demo workspace is warming up for the first time… give it ~15 seconds.");
        let attempts = 0;
        const poll = async () => {
          attempts += 1;
          try {
            await tryLogin();
            setErr("");
          } catch (ex2) {
            if (attempts < 8 && ex2?.response?.status === 503) {
              setTimeout(poll, 3000);
            } else {
              setErr(formatErr(
                ex2?.response?.data?.detail,
                "Demo couldn't start. Please refresh and try again in a minute.",
              ));
              setDemoBusy(false);
            }
          }
        };
        setTimeout(poll, 5000);
        return;
      }
      setErr(formatErr(ex?.response?.data?.detail, "Couldn't start the demo. Please try again."));
    } finally {
      // On 503 the polling loop manages busy state — don't clear early.
      // For other errors / success we clear here.
      if (!err.startsWith("Demo workspace is warming up")) setDemoBusy(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotErr("");
    if (!forgotEmail.trim()) {
      setForgotErr("Email is required.");
      return;
    }
    if (forgotNote.trim().length < 3) {
      setForgotErr("Please give a reason (at least 3 characters) so admin can verify it's you.");
      return;
    }
    if (forgotNewPassword.length < 4) {
      setForgotErr("Type the new password you'd like (at least 4 characters).");
      return;
    }
    setForgotBusy(true);
    try {
      await api.post("/auth/request-password-reset", {
        email: forgotEmail.trim(),
        reason: forgotNote.trim(),
        new_password: forgotNewPassword,
      });
      setForgotSent(true);
    } catch (ex) {
      setForgotErr(formatErr(ex?.response?.data?.detail, "Could not send request."));
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="min-h-screen hv-mh-dvh flex items-stretch hv-safe-top hv-safe-bottom hv-safe-x" data-testid="login-page">
      {/* Left: hero — branding-driven per tenant. HidroVerse gets its
          dark-water photos, automotive / etc tenants get their own
          imagery (set via Branding settings card). Falls back to
          neutral business imagery for un-customised tenants. */}
      <div className="hv-login-hero hidden md:flex relative flex-1 overflow-hidden">
        {(() => {
          // Priority order:
          //   1. branding.login_hero_dark_url / _light_url (per-tenant)
          //   2. host-pattern fallback for HV anchor (covers cold-load
          //      before branding hydrates)
          //   3. neutral Rogue defaults
          const heroDark = branding?.login_hero_dark_url
            || (isHidroVerseHost()
              ? "https://images.pexels.com/photos/18405255/pexels-photo-18405255.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
              : "https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200");
          const heroLight = branding?.login_hero_light_url
            || (isHidroVerseHost()
              ? "https://images.pexels.com/photos/2547565/pexels-photo-2547565.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
              : "https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200");
          return (
            <>
              <img src={heroDark} alt="" className="hv-login-hero-dark absolute inset-0 w-full h-full object-cover" />
              <img src={heroLight} alt="" className="hv-login-hero-light absolute inset-0 w-full h-full object-cover" />
            </>
          );
        })()}
        <div className="hv-login-hero-overlay absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/40" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex flex-col items-start gap-1">
            <img src={LOGO_URL} alt={BRAND_NAME} className="h-32 w-auto object-contain" />
          </div>

          <div className="max-w-md">
            <h1
              className="font-display text-7xl md:text-8xl font-bold tracking-tight leading-none text-white"
              style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}
            >
              {APP_TAIL ? (
                <>
                  {APP_HEAD}<span className="text-[var(--brand-primary,#1fb6c1)]">{APP_TAIL}</span>
                </>
              ) : (
                <span className="text-[var(--brand-primary,#1fb6c1)]">{APP_HEAD}</span>
              )}
            </h1>
            <h2
              className="mt-5 text-lg md:text-xl font-medium text-white/90"
              style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}
            >
              Customer Portal · <span className="text-[var(--brand-primary,#1fb6c1)]">Field Services</span> · CRM
            </h2>
            <p
              className="mt-3 text-sm text-[#a0aabf] leading-relaxed"
              style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}
            >
              Clients track quotes, installations and service history — and
              request support directly from their portal. Our team runs leads,
              dispatches technicians and closes the loop, all in one place.
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.18em] text-[#a0aabf]">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[var(--brand-primary,#1fb6c1)]/40 bg-[var(--brand-primary,#1fb6c1)]/10 text-[var(--brand-primary,#1fb6c1)]">
                <LifeBuoy className="w-3.5 h-3.5" />
                Request support
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10">
                View your jobs
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10">
                Track quotes
              </span>
            </div>
          </div>

          <div className="text-xs text-[#647087] tracking-wider uppercase">
            Trusted by {BRAND_NAME} clients &amp; crew
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-10 bg-[#0a0b0e] hv-grid-bg">
        {scopePicker ? (
          <CustomerScopePicker
            picker={scopePicker}
            loginScope={loginScope}
            navigate={navigate}
            onBack={() => { setScopePicker(null); setPassword(""); setErr(""); }}
            brandName={BRAND_NAME}
          />
        ) : claimMode ? (
          <form
            onSubmit={handleClaim}
            className="w-full max-w-md space-y-5"
            data-testid="claim-password-form"
          >
            <div className="md:hidden mb-4 flex flex-col items-center gap-1">
              <img src={LOGO_URL} alt={BRAND_NAME} className="h-24 w-auto object-contain" />
              <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--brand-primary,#1fb6c1)] font-semibold">
                {APP_NAME}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#647087] flex items-center gap-2">
                <KeyRound className="w-3.5 h-3.5" /> First sign-in
              </div>
              <h2 className="font-display text-3xl font-bold tracking-tight mt-1">
                Create your password.
              </h2>
                <p className="text-sm text-[#a0aabf] mt-1.5">
                  Welcome,{" "}
                  <span className="text-white font-medium">{email}</span>. Choose a
                  password you'll use every time you sign in. You can change it
                  later from Team Access.
                </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#a0aabf]">New password</label>
                <div className="relative mt-1">
                  <input
                    type={showPwd ? "text" : "password"}
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    required
                    autoFocus
                    data-testid="claim-password-new"
                    className="hv-input pr-10"
                    placeholder="At least 4 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    data-testid="claim-password-toggle"
                    aria-label={showPwd ? "Hide password" : "Show password"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#647087] hover:text-white p-1"
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Confirm password</label>
                <input
                  type={showPwd ? "text" : "password"}
                  value={newPw2}
                  onChange={(e) => setNewPw2(e.target.value)}
                  required
                  data-testid="claim-password-confirm"
                  className="hv-input mt-1"
                  placeholder="Type it again"
                />
              </div>
            </div>

            {err && (
              <div
                data-testid="claim-password-error"
                className="text-sm bg-rose-500/10 border border-rose-500/30 text-rose-200 rounded-md px-3 py-2"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="claim-password-submit"
              className="hv-btn hv-btn-primary w-full py-3"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create password & sign in"}
            </button>

            <button
              type="button"
              onClick={() => {
                setClaimMode(false);
                setErr("");
                setNewPw("");
                setNewPw2("");
              }}
              className="text-xs text-[#647087] hover:text-white w-full text-center"
              data-testid="claim-password-back"
            >
              ← Use a different email
            </button>
          </form>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="w-full max-w-md space-y-5"
            data-testid="login-form"
          >
            <div className="md:hidden mb-4 flex flex-col items-center gap-1">
              <img src={LOGO_URL} alt={BRAND_NAME} className="h-24 w-auto object-contain" />
              <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--brand-primary,#1fb6c1)] font-semibold">
                {APP_NAME}
              </div>
              <div className="text-[10px] text-[#647087] tracking-wider mt-1 text-center">
                Customer Portal · Field Services · CRM
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">Sign in</div>
              <h2 className="font-display text-3xl font-bold tracking-tight mt-1">
                Welcome back.
              </h2>
              <p className="text-sm text-[#a0aabf] mt-1.5">
                Clients and the {BRAND_NAME} team — sign in below.
              </p>
            </div>

            {sessionExpired && (
              <div
                data-testid="login-session-expired-banner"
                className="flex items-start gap-2.5 text-sm bg-amber-500/10 border border-amber-500/40 text-amber-200 rounded-md px-3 py-2.5"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium text-amber-100">Your session expired.</div>
                  <div className="text-xs text-amber-200/80 mt-0.5">
                    Please sign in again to continue. You can pick a longer
                    session length in Settings → Profile &amp; access →
                    Session length.
                  </div>
                </div>
              </div>
            )}

            {switchMode && !sessionExpired && (
              <div
                data-testid="login-switch-customer-banner"
                className="flex items-start gap-2.5 text-sm bg-[#1fb6c1]/10 border border-[#1fb6c1]/40 text-[#8ad0d6] rounded-md px-3 py-2.5"
              >
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium text-white">Signed out — pick a different customer.</div>
                  <div className="text-xs text-[#8ad0d6]/90 mt-0.5">
                    Sign in with the same email and we&apos;ll show your customer picker again.
                    Each session is hard-scoped by design.
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="hv-input mt-1"
                  placeholder="you@yourcompany.com"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Password</label>
                <div className="relative mt-1">
                  <input
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    className="hv-input pr-10"
                    placeholder="Leave blank for first sign-in"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    data-testid="login-password-toggle"
                    aria-label={showPwd ? "Hide password" : "Show password"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#647087] hover:text-white p-1"
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <label className="flex items-center gap-2 select-none cursor-pointer pt-1">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  data-testid="login-remember-checkbox"
                  className="w-4 h-4 accent-[#1fb6c1] rounded"
                />
                <span className="text-xs text-[#a0aabf]">Remember my email on this device</span>
              </label>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={openForgot}
                  className="text-xs text-[#1fb6c1] hover:text-white inline-flex items-center gap-1"
                  data-testid="login-forgot-password-btn"
                >
                  <HelpCircle className="w-3 h-3" /> Forgot my password
                </button>
              </div>
            </div>

            {err && (
              <div
                data-testid="login-error"
                className="text-sm bg-rose-500/10 border border-rose-500/30 text-rose-200 rounded-md px-3 py-2"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="hv-btn hv-btn-primary w-full py-3"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </button>
            {loadingHint && (
              <div
                className="text-xs text-[#9aa3b5] text-center -mt-1"
                data-testid="login-loading-hint"
              >
                {loadingHint}
              </div>
            )}

            {/* [2026-02-19 — Stage 2 part 3] Continue as demo. Logs in
                read-only as the seeded demo user so prospects can
                explore the full app without an account. Anchor tenants
                (Hidroverse production) can hide this with a feature
                flag if it feels off-brand — for now it's universally
                visible because it's a powerful conversion tool. */}
            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
              <div className="relative flex justify-center text-[10px] uppercase tracking-[0.3em] text-[#647087]">
                <span className="bg-[#0a0d12] px-2">or</span>
              </div>
            </div>
            <button
              type="button"
              onClick={continueAsDemo}
              disabled={demoBusy || loading}
              data-testid="login-demo-button"
              className="hv-btn hv-btn-ghost w-full py-3 inline-flex items-center justify-center gap-2"
              title="Explore the app with seeded demo data — read-only, no signup needed."
            >
              {demoBusy
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting demo…</>
                : <>Continue as demo · <span className="text-[var(--brand-primary,#1fb6c1)]">read-only tour</span></>}
            </button>

            <div className="flex justify-center pt-1">
              <InstallPWAButton variant="primary" className="!text-xs !py-2 !px-3" />
            </div>
          </form>
        )}

        {forgotOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6 hv-safe-top hv-safe-bottom hv-safe-x"
            onClick={(e) => {
              if (e.target === e.currentTarget) setForgotOpen(false);
            }}
            data-testid="forgot-password-modal"
          >
            <div
              className="w-full max-w-md bg-[#121419] border border-white/10 rounded-t-2xl md:rounded-lg overflow-hidden flex flex-col max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom))]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                <h3 className="font-display text-lg font-bold tracking-tight">
                  Request password reset
                </h3>
                <button
                  onClick={() => setForgotOpen(false)}
                  className="text-[#647087] hover:text-white transition"
                  data-testid="forgot-password-close"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              <div className="p-5 overflow-y-auto">
                {forgotSent ? (
                  <div className="space-y-4" data-testid="forgot-password-sent">
                    <div className="flex items-center gap-3 text-emerald-300">
                      <CheckCircle2 className="w-6 h-6" />
                      <div className="font-display text-lg font-bold text-white">
                        Request received.
                      </div>
                    </div>
                    <p className="text-sm text-[#a0aabf] leading-relaxed">
                      A {BRAND_NAME} admin has been notified and will reset your
                      password shortly. They'll reach out via WhatsApp, phone
                      or in person with your new temporary password.
                    </p>
                    <p className="text-xs text-[#647087]">
                      Tip: if it's urgent, message{" "}
                      <span className="text-white font-medium">{BRAND_NAME} support</span>{" "}
                      directly — the request is also visible in their Settings
                      → Pending requests panel.
                    </p>
                    <button
                      onClick={() => setForgotOpen(false)}
                      className="hv-btn hv-btn-primary w-full"
                      data-testid="forgot-password-done"
                    >
                      Got it
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submitForgot} className="space-y-4" data-testid="forgot-password-form">
                    <p className="text-sm text-[#a0aabf] leading-relaxed">
                      Enter your email below. An admin will verify your identity
                      and set a new password for you — no email-link required.
                    </p>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-[#a0aabf]">
                        Email
                      </label>
                      <input
                        type="email"
                        required
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="hv-input mt-1"
                        placeholder="you@yourcompany.com"
                        autoFocus
                        data-testid="forgot-password-email"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-[#a0aabf]">
                        Reason <span className="text-rose-300">*</span>
                      </label>
                      <textarea
                        value={forgotNote}
                        onChange={(e) => setForgotNote(e.target.value)}
                        className="hv-input mt-1 min-h-[60px]"
                        placeholder="Why do you need a reset? Helps admin verify it's really you."
                        data-testid="forgot-password-note"
                        required
                        minLength={3}
                      />
                    </div>
                    {/* Iter 418.aa — user types the exact password they
                        want to become theirs. Admin authorises it with
                        one click — no admin-side typing = no byte-
                        mismatch mysteries. */}
                    <div>
                      <label className="text-xs uppercase tracking-wider text-[#a0aabf]">
                        New password you&apos;d like <span className="text-rose-300">*</span>
                      </label>
                      <input
                        type="text"
                        value={forgotNewPassword}
                        onChange={(e) => setForgotNewPassword(e.target.value)}
                        className="hv-input mt-1 font-mono"
                        placeholder="Type the exact password you want to use"
                        data-testid="forgot-password-new"
                        minLength={4}
                        required
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck="false"
                      />
                      <div className="text-[10px] text-[#647087] mt-1">
                        Shown here so you can see exactly what you typed —
                        no hidden characters, no autocorrect. Once admin
                        approves, THIS is your login password.
                      </div>
                    </div>
                    {forgotErr && (
                      <div className="text-sm bg-rose-500/10 border border-rose-500/30 text-rose-200 rounded-md px-3 py-2">
                        {forgotErr}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={forgotBusy}
                      className="hv-btn hv-btn-primary w-full py-2.5"
                      data-testid="forgot-password-submit"
                    >
                      {forgotBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send request"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * Iter 426 — Customer scope picker.
 *
 * Rendered after /auth/login returns { scope_required: true, customers }
 * for a portal user who is a member of >1 customers. Picking a card
 * calls /auth/login/scope with the chosen customer_id (re-verifying
 * the password server-side) and mints a token cryptographically
 * bound to that customer. Sign-out is required to switch — no
 * in-app switcher exists by design.
 */
function CustomerScopePicker({ picker, loginScope, navigate, onBack, brandName }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [pickedId, setPickedId] = useState(null);

  const pick = async (cid) => {
    setPickedId(cid);
    setErr("");
    setBusy(true);
    try {
      const u = await loginScope(picker.email, picker._password, cid);
      const target = u?.role === "client" ? "/portal" : "/";
      navigate(target, { replace: true });
    } catch (ex) {
      setPickedId(null);
      const status = ex?.response?.status;
      const detail = ex?.response?.data?.detail;
      if (status === 401) setErr("Password verification failed — please sign in again.");
      else if (status === 403) setErr(typeof detail === "string" ? detail : "You do not have access to that customer.");
      else setErr(formatErr(detail, "Could not sign in to that customer."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full max-w-md space-y-5" data-testid="scope-picker">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">Pick a workspace</div>
        <h2 className="font-display text-3xl font-bold tracking-tight mt-1">
          Which customer today?
        </h2>
        <p className="text-sm text-[#a0aabf] mt-1.5">
          You&apos;re a member of{" "}
          <span className="text-white font-medium">{picker.customers.length} customers</span>.
          Pick one — the session is locked to that customer until you sign out.
        </p>
      </div>

      {err && (
        <div className="text-sm bg-rose-500/10 border border-rose-500/30 text-rose-200 rounded-md px-3 py-2"
          data-testid="scope-picker-error">
          {err}
        </div>
      )}

      <div className="space-y-2">
        {picker.customers.map((c) => (
          <button
            key={c.customer_id}
            type="button"
            onClick={() => pick(c.customer_id)}
            disabled={busy}
            data-testid={`scope-picker-card-${c.customer_id}`}
            className="w-full flex items-center gap-3 p-3 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-[var(--brand-primary,#1fb6c1)]/50 transition text-left disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[var(--brand-primary,#1fb6c1)] to-[#7c3aed] flex items-center justify-center text-lg font-bold uppercase text-[#0a0b0e] shrink-0">
              {c.logo_url
                ? <img src={c.logo_url} alt={c.name} className="w-full h-full object-cover rounded-md" />
                : ((c.name || "?").slice(0, 1))}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white truncate">{c.name}</div>
              {c.customer_role && (
                <div className="text-[10px] uppercase tracking-wider text-[#647087] mt-0.5">
                  {c.customer_role.replace("customer_", "").replace(/_/g, " ")}
                </div>
              )}
            </div>
            {busy && pickedId === c.customer_id && (
              <Loader2 className="w-4 h-4 animate-spin text-[var(--brand-primary,#1fb6c1)]" />
            )}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={onBack}
        disabled={busy}
        data-testid="scope-picker-back"
        className="text-xs text-[#647087] hover:text-white w-full text-center pt-2"
      >
        ← Use a different email
      </button>

      <p className="text-[10px] text-[#647087] text-center leading-relaxed pt-2">
        Sign out to switch customer at any time. {brandName} never mixes data
        across customers — each session is hard-scoped by design.
      </p>
    </div>
  );
}
